import { gql, TypedDocumentNode } from '@apollo/client';

import { BASE_TASK_FIELD } from '@atom/graph/task';
import { BASE_WORK_ORDER_FIELD } from '@atom/graph/work';
import { TaskField, TaskMaterialItem } from '@atom/types/task';
import { WorkOrderField } from '@atom/types/work';
import {
  SearchWorkTemplatesConnection,
  WorkOrderTemplateAssigneeCreateInput,
  WorkOrderTemplateAssigneeDeleteInput,
  WorkOrderTemplateCreateInput,
  WorkOrderTemplateFieldCreateInput,
  WorkOrderTemplateFieldDeleteInput,
  WorkOrderTemplateFieldUpdateInput,
  WorkOrderTemplateFoldersConnection,
  WorkOrderTemplateFoldersConnectionInput,
  WorkOrderTemplateFormCreateInput,
  WorkOrderTemplateFormDeleteInput,
  WorkOrderTemplateMaterialCreateInput,
  WorkOrderTemplateMaterialDeleteInput,
  WorkOrderTemplatesConnectionInput,
  WorkOrderTemplateSearchInput,
  WorkOrderTemplateTaskCreateInput,
  WorkOrderTemplateTaskDeleteInput,
  WorkOrderTemplateTaskDuplicateInput,
  WorkOrderTemplateTaskFieldCreateInput,
  WorkOrderTemplateTaskFieldDeleteInput,
  WorkOrderTemplateTaskFieldUpdateInput,
  WorkOrderTemplateTaskUpdateInput,
  WorkOrderTemplateUpdateInput,
  WorkOrderTemplateUserGroupCreateInput,
  WorkOrderTemplateUserGroupDeleteInput,
  WorkTemplate,
  WorkTemplatesConnection,
  WorkTemplateTaskItem,
} from '@atom/types/workTemplate';

const BASE_WORK_ORDER_TEMPLATE_TASK = gql`
  fragment BaseWorkTemplateTask on WorkOrderTemplateTask {
    id
    name
    inheritedComponents {
      type
      parentId
      ids
      required
    }
    description
    dueDate
    estimatedCost
    isEstimatedCostOverridden
    userIds
    users {
      id
      firstName
      lastName
      email
      photoUrl
    }
    formTemplateIds
    formTemplates {
      id
      name
    }
    materials {
      assetId
      name
      schemaId
      colorId
      markerId
      locationType
      isStockBased
      rate
      cost
      quantity
      unit
    }
    userGroups {
      userGroupId
      name
      colorId
      quantity
      userCount
      workTime
      budget {
        budgetId
        rate
        restored
        reopenedWorkOrderIds
      }
    }
    categoryIds
    categories {
      id
      name
      categoryPath
    }
    schemaIds
    schemas {
      id
      name
      markerId
      colorId
      ancestors
      isMaterial
      locationType
    }
    fields {
      ...BaseTaskField
    }
    fieldOrder
    modules
    createdDate
    locationsEnabled
    requireAtLeastOneLocation
    multiAssetsEnabled
    allowedMultiAssetSchemaIds
    allowedMultiAssetSchemas {
      id
      name
      markerId
      colorId
      ancestors
    }
    requireAtLeastOneMultiAsset
    requiredMultiAssetSchemaIds
    requiredMultiAssetSchemas {
      id
      name
      markerId
      colorId
      ancestors
    }
  }
  ${BASE_TASK_FIELD}
`;

const BASE_WORK_ORDER_TEMPLATE = gql`
  fragment BaseWorkOrderTemplate on WorkOrderTemplate {
    id
    type
    externalId
    published
    appointmentsEnabled
    preferences {
      disableTaskCreation
      disableTaskDuplication
      dateTimeEnabled
    }
    parentId
    isParent
    inheritedComponents {
      type
      parentId
      ids
      required
    }
    requiredInheritedComponents
    name
    schemaId
    estimatedCost
    priorityId
    leadAssigneeId
    tasks {
      ...BaseWorkTemplateTask
    }
    fields {
      ...BaseWorkOrderField
    }
    fieldOrder
    folderPath
    summary {
      title
      contents
    }
    media {
      fileId
      name
      type
      fileExtension
      mediaId
    }
    createdDate
    createdBy {
      id
      firstName
      lastName
      photoUrl
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
  ${BASE_WORK_ORDER_FIELD}
`;

export const GET_WORK_ORDER_TEMPLATE: TypedDocumentNode<
  { workOrderTemplate: WorkTemplate },
  { id: string; showAdmin?: boolean }
> = gql`
  query workOrderTemplate($id: ID!, $showAdmin: Boolean) {
    workOrderTemplate(id: $id, showAdmin: $showAdmin) {
      ...BaseWorkOrderTemplate
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE}
`;

export const GET_WORK_ORDER_TEMPLATE_FOLDERS: TypedDocumentNode<
  { workOrderTemplateFolders: WorkOrderTemplateFoldersConnection },
  { input: WorkOrderTemplateFoldersConnectionInput }
> = gql`
  query workOrderTemplateFolders(
    $input: WorkOrderTemplateFoldersConnectionInput
  ) {
    workOrderTemplateFolders(input: $input) {
      totalCount
      workOrderTemplateFolders {
        id
        name
        parentFolderId
        ancestors
        hasTemplates
        createdDate
        updatedDate
        createdBy
        updatedBy
      }
    }
  }
`;

export const GET_WORK_ORDER_TEMPLATES: TypedDocumentNode<
  { workOrderTemplates: WorkTemplatesConnection },
  { input: WorkOrderTemplatesConnectionInput }
> = gql`
  query workOrderTemplates($input: WorkOrderTemplatesConnectionInput) {
    workOrderTemplates(input: $input) {
      totalCount
      workOrderTemplates {
        id
        name
        published
        appointmentsEnabled
        preferences {
          disableTaskCreation
          disableTaskDuplication
          dateTimeEnabled
        }
        type
        schemaId
        schemaName
        parentId
        isParent
        parentName
        priorityId
        createdDate
        createdBy {
          id
          firstName
          lastName
          photoUrl
        }
      }
    }
  }
`;

export const WORK_TEMPLATE_SEARCH: TypedDocumentNode<
  { workOrderTemplateSearch: SearchWorkTemplatesConnection },
  { input: WorkOrderTemplateSearchInput }
> = gql`
  query workOrderTemplateSearch($input: WorkOrderTemplateSearchInput!) {
    workOrderTemplateSearch(input: $input) {
      totalCount
      workOrderTemplates {
        id
        name
        schemaId
        parentId
        isParent
        appointmentsEnabled
        preferences {
          disableTaskCreation
          disableTaskDuplication
          dateTimeEnabled
        }
        type
        folderPath {
          id
          name
        }
      }
    }
  }
`;

export const WORK_TEMPLATE_CREATE: TypedDocumentNode<
  { workOrderTemplateCreate: WorkTemplate },
  { input: WorkOrderTemplateCreateInput }
> = gql`
  mutation workOrderTemplateCreate($input: WorkOrderTemplateCreateInput!) {
    workOrderTemplateCreate(input: $input) {
      ...BaseWorkOrderTemplate
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE}
`;

export const WORK_TEMPLATE_UPDATE: TypedDocumentNode<
  { workOrderTemplateUpdate: WorkTemplate },
  { input: WorkOrderTemplateUpdateInput }
> = gql`
  mutation workOrderTemplateUpdate($input: WorkOrderTemplateUpdateInput!) {
    workOrderTemplateUpdate(input: $input) {
      ...BaseWorkOrderTemplate
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE}
`;

export const WORK_TEMPLATE_DUPLICATE: TypedDocumentNode<
  { workOrderTemplateDuplicate: { id: string; name: string } },
  { id: string }
> = gql`
  mutation workOrderTemplateDuplicate($id: ID!) {
    workOrderTemplateDuplicate(id: $id) {
      id
      name
    }
  }
`;

export const WORK_TEMPLATE_DELETE: TypedDocumentNode<
  { workOrderTemplateDelete: boolean },
  { id: string }
> = gql`
  mutation workOrderTemplateDelete($id: ID!) {
    workOrderTemplateDelete(id: $id)
  }
`;

export const WORK_TEMPLATE_FIELD_CREATE: TypedDocumentNode<
  { workOrderTemplateFieldCreate: WorkOrderField },
  { input: WorkOrderTemplateFieldCreateInput }
> = gql`
  mutation workOrderTemplateFieldCreate(
    $input: WorkOrderTemplateFieldCreateInput!
  ) {
    workOrderTemplateFieldCreate(input: $input) {
      ...BaseWorkOrderField
    }
  }
  ${BASE_WORK_ORDER_FIELD}
`;

export const WORK_TEMPLATE_FIELD_UPDATE: TypedDocumentNode<
  { workOrderTemplateFieldUpdate: WorkOrderField },
  { input: WorkOrderTemplateFieldUpdateInput }
> = gql`
  mutation workOrderTemplateFieldUpdate(
    $input: WorkOrderTemplateFieldUpdateInput!
  ) {
    workOrderTemplateFieldUpdate(input: $input) {
      ...BaseWorkOrderField
    }
  }
  ${BASE_WORK_ORDER_FIELD}
`;

export const WORK_TEMPLATE_FIELD_DELETE: TypedDocumentNode<
  { workOrderTemplateFieldDelete: boolean },
  { input: WorkOrderTemplateFieldDeleteInput }
> = gql`
  mutation workOrderTemplateFieldDelete(
    $input: WorkOrderTemplateFieldDeleteInput!
  ) {
    workOrderTemplateFieldDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_CREATE: TypedDocumentNode<
  { workOrderTemplateTaskCreate: WorkTemplateTaskItem },
  { input: WorkOrderTemplateTaskCreateInput }
> = gql`
  mutation workOrderTemplateTaskCreate(
    $input: WorkOrderTemplateTaskCreateInput!
  ) {
    workOrderTemplateTaskCreate(input: $input) {
      ...BaseWorkTemplateTask
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
`;

export const WORK_TEMPLATE_TASK_UPDATE: TypedDocumentNode<
  { workOrderTemplateTaskUpdate: WorkTemplateTaskItem },
  { input: WorkOrderTemplateTaskUpdateInput }
> = gql`
  mutation workOrderTemplateTaskUpdate(
    $input: WorkOrderTemplateTaskUpdateInput!
  ) {
    workOrderTemplateTaskUpdate(input: $input) {
      ...BaseWorkTemplateTask
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
`;

export const WORK_TEMPLATE_TASK_DELETE: TypedDocumentNode<
  { workOrderTemplateTaskDelete: boolean },
  { input: WorkOrderTemplateTaskDeleteInput }
> = gql`
  mutation workOrderTemplateTaskDelete(
    $input: WorkOrderTemplateTaskDeleteInput!
  ) {
    workOrderTemplateTaskDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_DUPLICATE: TypedDocumentNode<
  { workOrderTemplateTaskDuplicate: boolean },
  { input: WorkOrderTemplateTaskDuplicateInput }
> = gql`
  mutation workOrderTemplateTaskDuplicate(
    $input: WorkOrderTemplateTaskDuplicateInput!
  ) {
    workOrderTemplateTaskDuplicate(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_FIELD_CREATE: TypedDocumentNode<
  { workOrderTemplateTaskFieldCreate: TaskField },
  { input: WorkOrderTemplateTaskFieldCreateInput }
> = gql`
  mutation workOrderTemplateTaskFieldCreate(
    $input: WorkOrderTemplateTaskFieldCreateInput!
  ) {
    workOrderTemplateTaskFieldCreate(input: $input) {
      ...BaseTaskField
    }
  }
  ${BASE_TASK_FIELD}
`;

export const WORK_TEMPLATE_TASK_FIELD_UPDATE: TypedDocumentNode<
  { workOrderTemplateTaskFieldUpdate: TaskField },
  { input: WorkOrderTemplateTaskFieldUpdateInput }
> = gql`
  mutation workOrderTemplateTaskFieldUpdate(
    $input: WorkOrderTemplateTaskFieldUpdateInput!
  ) {
    workOrderTemplateTaskFieldUpdate(input: $input) {
      ...BaseTaskField
    }
  }
  ${BASE_TASK_FIELD}
`;

export const WORK_TEMPLATE_TASK_FIELD_DELETE: TypedDocumentNode<
  { workOrderTemplateTaskFieldDelete: boolean },
  { input: WorkOrderTemplateTaskFieldDeleteInput }
> = gql`
  mutation workOrderTemplateTaskFieldDelete(
    $input: WorkOrderTemplateTaskFieldDeleteInput!
  ) {
    workOrderTemplateTaskFieldDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_ASSIGNEE_CREATE: TypedDocumentNode<
  { workOrderTemplateAssigneeCreate: WorkTemplateTaskItem },
  { input: WorkOrderTemplateAssigneeCreateInput }
> = gql`
  mutation workOrderTemplateAssigneeCreate(
    $input: WorkOrderTemplateAssigneeCreateInput!
  ) {
    workOrderTemplateAssigneeCreate(input: $input) {
      ...BaseWorkTemplateTask
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
`;

export const WORK_TEMPLATE_TASK_ASSIGNEE_DELETE: TypedDocumentNode<
  { workOrderTemplateAssigneeDelete: boolean },
  { input: WorkOrderTemplateAssigneeDeleteInput }
> = gql`
  mutation workOrderTemplateAssigneeDelete(
    $input: WorkOrderTemplateAssigneeDeleteInput!
  ) {
    workOrderTemplateAssigneeDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_USER_GROUP_CREATE: TypedDocumentNode<
  { workOrderTemplateUserGroupCreate: WorkTemplateTaskItem },
  { input: WorkOrderTemplateUserGroupCreateInput }
> = gql`
  mutation workOrderTemplateUserGroupCreate(
    $input: WorkOrderTemplateUserGroupCreateInput!
  ) {
    workOrderTemplateUserGroupCreate(input: $input) {
      ...BaseWorkTemplateTask
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
`;

export const WORK_TEMPLATE_TASK_USER_GROUP_DELETE: TypedDocumentNode<
  { workOrderTemplateUserGroupDelete: boolean },
  { input: WorkOrderTemplateUserGroupDeleteInput }
> = gql`
  mutation workOrderTemplateUserGroupDelete(
    $input: WorkOrderTemplateUserGroupDeleteInput!
  ) {
    workOrderTemplateUserGroupDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_MATERIAL_CREATE: TypedDocumentNode<
  { workOrderTemplateMaterialCreate: TaskMaterialItem },
  { input: WorkOrderTemplateMaterialCreateInput }
> = gql`
  mutation workOrderTemplateMaterialCreate(
    $input: WorkOrderTemplateMaterialCreateInput!
  ) {
    workOrderTemplateMaterialCreate(input: $input) {
      assetId
      name
      schemaId
      colorId
      isStockBased
      rate
      cost
      quantity
      unit
    }
  }
`;

export const WORK_TEMPLATE_TASK_MATERIAL_DELETE: TypedDocumentNode<
  { workOrderTemplateMaterialDelete: boolean },
  { input: WorkOrderTemplateMaterialDeleteInput }
> = gql`
  mutation workOrderTemplateMaterialDelete(
    $input: WorkOrderTemplateMaterialDeleteInput!
  ) {
    workOrderTemplateMaterialDelete(input: $input)
  }
`;

export const WORK_TEMPLATE_TASK_FORM_TEMPLATE_CREATE: TypedDocumentNode<
  { workOrderTemplateFormCreate: WorkTemplateTaskItem },
  { input: WorkOrderTemplateFormCreateInput }
> = gql`
  mutation workOrderTemplateFormCreate(
    $input: WorkOrderTemplateFormCreateInput!
  ) {
    workOrderTemplateFormCreate(input: $input) {
      ...BaseWorkTemplateTask
    }
  }
  ${BASE_WORK_ORDER_TEMPLATE_TASK}
`;

export const WORK_TEMPLATE_TASK_FORM_TEMPLATE_DELETE: TypedDocumentNode<
  { workOrderTemplateFormDelete: boolean },
  { input: WorkOrderTemplateFormDeleteInput }
> = gql`
  mutation workOrderTemplateFormDelete(
    $input: WorkOrderTemplateFormDeleteInput!
  ) {
    workOrderTemplateFormDelete(input: $input)
  }
`;
